/**
 * Firebase Authentication Service
 */

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  EmailAuthProvider,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  updatePassword,
  reauthenticateWithCredential,
  linkWithCredential,
  User,
  UserCredential,
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { getFirebaseAuth, getFirebaseFirestore, isFirebaseConfigured } from './firebase/config';

// Types
export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
  createdAt: string | null;
}

export interface AuthError {
  code: string;
  message: string;
}

// Google Provider
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');

/**
 * Sign in with email and password
 */
export async function signInWithEmail(email: string, password: string): Promise<UserCredential> {
  const auth = getFirebaseAuth();
  return signInWithEmailAndPassword(auth, email, password);
}

/**
 * Sign up with email and password
 */
export async function signUpWithEmail(
  email: string,
  password: string,
  displayName?: string
): Promise<UserCredential> {
  const auth = getFirebaseAuth();
  const credential = await createUserWithEmailAndPassword(auth, email, password);

  // Update display name if provided
  if (displayName && credential.user) {
    await updateProfile(credential.user, { displayName });
  }

  // Create user document in Firestore
  await createUserDocument(credential.user);

  return credential;
}

/**
 * Sign in with Google
 */
export async function signInWithGoogle(): Promise<UserCredential> {
  const auth = getFirebaseAuth();
  const credential = await signInWithPopup(auth, googleProvider);

  // Create or update user document
  await createUserDocument(credential.user);

  return credential;
}

/**
 * Sign out
 */
export async function logOut(): Promise<void> {
  const auth = getFirebaseAuth();
  return signOut(auth);
}

/**
 * Send password reset email
 */
export async function resetPassword(email: string): Promise<void> {
  const auth = getFirebaseAuth();
  return sendPasswordResetEmail(auth, email);
}

/**
 * Subscribe to auth state changes
 */
export function subscribeToAuthState(callback: (user: User | null) => void): () => void {
  const auth = getFirebaseAuth();
  return onAuthStateChanged(auth, callback);
}

/**
 * Get current user
 */
export function getCurrentUser(): User | null {
  const auth = getFirebaseAuth();
  return auth.currentUser;
}

/**
 * Create user document in Firestore
 */
async function createUserDocument(user: User): Promise<void> {
  if (!user) return;

  const db = getFirebaseFirestore();
  const userRef = doc(db, 'users', user.uid);
  const userSnap = await getDoc(userRef);

  if (!userSnap.exists()) {
    // Create new user document
    await setDoc(userRef, {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      emailVerified: user.emailVerified,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      provider: user.providerData[0]?.providerId || 'email',
      plan: 'free',
      settings: {
        theme: 'dark',
        notifications: true,
      },
    });
  } else {
    // Update last login
    await setDoc(
      userRef,
      {
        lastLoginAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  }
}

/**
 * Convert Firebase User to AuthUser
 */
export function toAuthUser(user: User | null): AuthUser | null {
  if (!user) return null;

  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
    emailVerified: user.emailVerified,
    createdAt: user.metadata.creationTime || null,
  };
}

/**
 * Get error message from Firebase error code
 */
export function getAuthErrorMessage(code: string): string {
  const errorMessages: Record<string, string> = {
    'auth/email-already-in-use': 'This email is already registered. Please sign in instead.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/operation-not-allowed': 'This sign-in method is not enabled.',
    'auth/weak-password': 'Password should be at least 6 characters.',
    'auth/user-disabled': 'This account has been disabled.',
    'auth/user-not-found': 'No account found with this email.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
    'auth/popup-closed-by-user': 'Sign-in popup was closed. Please try again.',
    'auth/cancelled-popup-request': 'Sign-in was cancelled.',
    'auth/popup-blocked': 'Sign-in popup was blocked. Please allow popups for this site.',
    'auth/network-request-failed': 'Network error. Please check your connection.',
  };

  return errorMessages[code] || 'An error occurred. Please try again.';
}

/**
 * Update user profile
 */
export async function updateUserProfile(data: { displayName?: string; photoURL?: string }): Promise<void> {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('No authenticated user');
  }

  await updateProfile(user, data);

  // Update Firestore document
  const db = getFirebaseFirestore();
  const userRef = doc(db, 'users', user.uid);
  await setDoc(
    userRef,
    {
      ...data,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

/**
 * Check if Firebase is ready
 */
export { isFirebaseConfigured };

/**
 * Check if user has password provider (email/password sign-in)
 */
export function hasPasswordProvider(user: User | null): boolean {
  if (!user) return false;
  return user.providerData.some((provider) => provider.providerId === 'password');
}

/**
 * Check if user signed up with Google
 */
export function hasGoogleProvider(user: User | null): boolean {
  if (!user) return false;
  return user.providerData.some((provider) => provider.providerId === 'google.com');
}

/**
 * Change password for users who already have a password
 */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;

  if (!user || !user.email) {
    throw new Error('No authenticated user');
  }

  // Re-authenticate with current password
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, credential);

  // Update to new password
  await updatePassword(user, newPassword);
}

/**
 * Set password for Google-only users (link email/password to account)
 */
export async function setPasswordForGoogleUser(newPassword: string): Promise<void> {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;

  if (!user || !user.email) {
    throw new Error('No authenticated user');
  }

  // Create email/password credential and link to account
  const credential = EmailAuthProvider.credential(user.email, newPassword);
  await linkWithCredential(user, credential);
}
